import { useMemo, useRef, type FC } from 'react'
import Editor, { Monaco } from '@monaco-editor/react'
import { webchatClasses } from '@botpress/webchat'
import { getUseConfigStore } from '~/features/webchat/stores/useConfigStore'
import { useLocalStore } from '~/stores'
import useNavigationBlocker, { navigationBlockerOptions } from '../../hooks/useNavigationBlocker'
import { DEFAULT_CUSTOM_STYLESHEET } from '../../utils/const'

type StandaloneEditor = ReturnType<Monaco['editor']['create']>

type Props = {
  workspaceId: string
  botId: string
}

export const CustomStylesSection: FC<Props> = ({ workspaceId, botId }) => {
  const useConfigStore = useMemo(() => getUseConfigStore({ botId, workspaceId }), [botId, workspaceId])

  const customStylesheet = useConfigStore((state) => state.customStyleSheet)
  const setCustomStylesheet = useConfigStore((state) => state.setCustomStyleSheet)

  const saveConfig = useConfigStore((state) => state.saveConfig)
  const _originalConfig = useConfigStore((state) => state._originalConfig)
  const hasPendingChanges = JSON.stringify(customStylesheet) !== JSON.stringify(_originalConfig?.customStyleSheet)

  useNavigationBlocker(hasPendingChanges, {
    ...navigationBlockerOptions,
    confirmCallback: async () => {
      try {
        await saveConfig()
        return true
      } catch (error) {
        console.log('Failed to save changes', error)
        return false
      }
    },
    cancelCallback: async () => {
      if (_originalConfig) {
        setCustomStylesheet(_originalConfig.customStyleSheet)
      }
      return false
    },
  })

  const darkMode = useLocalStore((s) => s.theme)
  const editorRef = useRef<StandaloneEditor | null>(null)

  const handleEditorDidMount = (editor: StandaloneEditor, monaco: Monaco) => {
    editorRef.current = editor

    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () => {
      formatEditor()
    })

    monaco.languages.css.cssDefaults.setOptions({
      validate: true,
    })

    monaco.languages.registerCompletionItemProvider('css', {
      provideCompletionItems: () => {
        const suggestions: any[] = []

        function traverseClasses(obj: Record<string, any>) {
          for (const key in obj) {
            const value = obj[key]
            if (value && typeof value === 'object') {
              traverseClasses(value)
            } else if (key === 'className' && typeof value === 'string') {
              const classes = value.split(' ')
              for (const className of classes) {
                if (!suggestions.some((s) => s.label === `.${className}`)) {
                  suggestions.push({
                    label: `.${className}`,
                    kind: monaco.languages.CompletionItemKind.Class,
                    insertText: `.${className}`,
                  })
                }
              }
            }
          }
        }
        traverseClasses(webchatClasses)

        return { suggestions }
      },
    })
  }

  const formatEditor = () => {
    editorRef.current?.getAction('editor.action.formatDocument')?.run()
  }

  return (
    <>
      <div className="flex flex-col gap-2">
        <h3 className="text-lg font-medium">Styles</h3>
        <p className="text-sm text-muted-foreground">Custom CSS Styles for your webchat.</p>
      </div>
      <div className="rounded-sm border border-neutral-300 py-1">
        <Editor
          height={560}
          language="css"
          theme={darkMode === 'dark' ? 'vs-dark' : 'light'}
          defaultValue={DEFAULT_CUSTOM_STYLESHEET}
          value={customStylesheet}
          onChange={setCustomStylesheet}
          onMount={handleEditorDidMount}
        />
      </div>
    </>
  )
}
